import { useEffect, useMemo, useState } from 'react';
import '../css/Passengers.css';

import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import useIdleLogout from "../hooks/useIdleLogout";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

export default function Passengers() {
  useIdleLogout(); 
  const [passengers, setPassengers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');

  // keep charts synced with theme
  const [isDark, setIsDark] = useState(
    typeof document !== 'undefined' && document.body.classList.contains('dark-mode')
  );
  useEffect(() => {
    const body = document.body;
    const mo = new MutationObserver(() => setIsDark(body.classList.contains('dark-mode')));
    mo.observe(body, { attributes: true, attributeFilter: ['class'] });
    return () => mo.disconnect();
  }, []);

  useEffect(() => {
    setPassengers([
      { id: 1, name: 'Sonam Wangdi',  email: 'sonam@example.com',  phone: '+975 17123456', status: 'Active',   rides: 34 },
      { id: 2, name: 'Kinley Tashi',  email: 'kinley@example.com', phone: '+975 17123457', status: 'Inactive', rides: 27 },
      { id: 3, name: 'Choden Zangmo', email: 'choden@example.com', phone: '+975 17123458', status: 'Active',   rides: 19 },
      { id: 4, name: 'Dorji Wangchuk',email: 'dorji@example.com',  phone: '+975 17123459', status: 'Active',   rides: 12 },
      { id: 5, name: 'Tandin Pem',    email: 'tandin@example.com', phone: '+975 17123460', status: 'Inactive', rides: 8  }
    ]);
  }, []);

  const filtered = passengers.filter(p => {
    const s = searchTerm.trim().toLowerCase();
    const matches =
      !s ||
      p.name.toLowerCase().includes(s) ||
      p.email.toLowerCase().includes(s) ||
      p.phone.toLowerCase().includes(s);
    return matches && (filterStatus === 'All' || p.status === filterStatus);
  });

  const total    = passengers.length;
  const active   = passengers.filter(p => p.status === 'Active').length;
  const inactive = passengers.filter(p => p.status === 'Inactive').length;

  // chart colors
  const cText     = isDark ? '#E6E6E6' : '#212529';
  const cGrid     = isDark ? '#2B2B2B' : '#E9ECEF';
  const cCard     = isDark ? '#1B1B1B' : '#FFFFFF';
  const cTooltipB = isDark ? 'rgba(0,0,0,.92)' : '#FFFFFF';
  const cTooltipT = isDark ? '#FFFFFF' : '#111111';

  // Doughnut
  const doughnutData = useMemo(() => ({
    labels: ['Active', 'Inactive'],
    datasets: [{
      data: [active, inactive],
      backgroundColor: ['#3ea0ff', '#ff6b81'], // brighter colors
      borderColor: cCard,
      borderWidth: 3,
      hoverOffset: 6
    }]
  }), [active, inactive, cCard]);

  const doughnutOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: true,
    cutout: '58%',
    plugins: {
      legend: { position: 'bottom', labels: { color: cText, boxWidth: 24, padding: 14 } },
      tooltip: {
        backgroundColor: cTooltipB,
        titleColor: cTooltipT,
        bodyColor: cTooltipT,
        borderWidth: 1,
        borderColor: isDark ? '#333' : '#ddd',
        callbacks: {
          label: (ctx) => {
            const label = ctx.label || '';
            const v = ctx.parsed;
            return ` ${label}: ${v}`;
          }
        }
      }
    },
    elements: {
      arc: {
        borderJoinStyle: 'round'
      }
    }
  }), [cText, cTooltipB, cTooltipT, isDark]);

  // Bar (top-5)
  const topLabels = useMemo(
    () => passengers.slice().sort((a,b)=>b.rides - a.rides).slice(0,5).map(p=>p.name),
    [passengers]
  );
  const barData = useMemo(() => ({
    labels: topLabels,
    datasets: [{
      label: 'Rides',
      data: topLabels.map(n => passengers.find(p=>p.name===n)?.rides ?? 0),
      backgroundColor: '#0d6efd',
      borderColor: '#0a58ca',
      borderWidth: 1,
      borderRadius: 6,
      barPercentage: 0.7,
      categoryPercentage: 0.7
    }]
  }), [topLabels, passengers]);

  // draw values above bars
  const valueLabelPlugin = useMemo(() => ({
    id: 'valueLabel',
    afterDatasetsDraw: (chart) => {
      const { ctx } = chart;
      const ds = chart.data.datasets[0];
      const meta = chart.getDatasetMeta(0);
      ctx.save();
      ctx.fillStyle = isDark ? '#FFFFFF' : '#1F2937';
      ctx.font = '12px system-ui, Segoe UI, Roboto, Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      meta.data.forEach((bar, i) => {
        const val = ds.data[i];
        if (val == null) return;
        const { x, y } = bar.tooltipPosition();
        ctx.fillText(val, x, y - 6);
      });
      ctx.restore();
    }
  }), [isDark]);

  const barOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: cTooltipB,
        titleColor: cTooltipT,
        bodyColor: cTooltipT,
        borderWidth: 1,
        borderColor: isDark ? '#333' : '#ddd'
      }
    },
    scales: {
      x: { ticks: { color: cText }, grid: { color: cGrid, drawBorder: false } },
      y: { ticks: { color: cText, precision: 0 }, grid: { color: cGrid, drawBorder: false } }
    }
  }), [cText, cGrid, cTooltipB, cTooltipT, isDark]);

  return (
    <>
      {/* Metrics */}
      <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4 mb-4">
        {[
          { label:'Total Registered Passengers', value: total },
          { label:'Active Passengers',           value: active },
          { label:'Inactive Passengers',         value: inactive }
        ].map((m,i)=>(
          <div className="col" key={i}>
            <div className="card h-100">
              <div className="card-body text-center">
                <h6 className="text-secondary fw-medium">{m.label}</h6>
                <p className="display-6 fw-semibold mb-0">{m.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="row gx-4 mb-4">
        <div className="col-md-6">
          <div className="card chart-card h-100 p-3">
            <h5>Rider Status Distribution</h5>
            <Doughnut data={doughnutData} options={doughnutOptions} />
          </div>
        </div>
        <div className="col-md-6">
          <div className="card chart-card h-100 p-3">
            <h5>Rides per Passenger (Top 5)</h5>
            <Bar data={barData} options={barOptions} plugins={[valueLabelPlugin]} />
          </div>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="card mb-4 search-filter-card">
        <div className="card-body d-flex align-items-center">
          <div className="input-group me-3 flex-grow-1">
            <span className="input-group-text"><i className="bi bi-search"></i></span>
            <input
              type="text"
              className="form-control"
              placeholder="Search passengers..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            className="form-select"
            style={{ maxWidth: '200px' }}
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
          >
            <option value="All">All Statuses</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
            <option value="Suspended">Suspended</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="table-responsive mb-4 card p-3 passengers-table-card">
        <table className="table table-borderless align-middle mb-0 table-pro table-hover table-striped">
          <colgroup>
            <col style={{width:'70px'}} />
            <col style={{width:'80px'}} />
            <col style={{width:'22%'}} />
            <col style={{width:'28%'}} />
            <col style={{width:'16%'}} />
            <col style={{width:'12%'}} />
            <col style={{width:'120px'}} />
          </colgroup>

          <thead>
            <tr>
              <th>Sl.No</th>
              <th>Photo</th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {filtered.map((p,i)=>(
              <tr key={p.id}>
                <td>{i+1}</td>
                <td>
                  <img
                    src="/assets/img/admin profile.jpg"
                    alt={p.name}
                    className="rounded-circle object-cover"
                    width="40"
                    height="40"
                  />
                </td>
                <td className="name-cell">{p.name}</td>
                <td className="email-cell" title={p.email}>{p.email}</td>
                <td className="phone-cell">{p.phone}</td>
                <td>
                  <span className={`badge bg-${p.status==='Active'?'success':'secondary'}`}>
                    {p.status}
                  </span>
                </td>
                <td>
                  <button className="btn btn-view" aria-label="View"><i className="bi bi-eye"/></button>
                  <button className="btn btn-delete" aria-label="Delete"><i className="bi bi-trash"/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <footer className="dashboard-footer mt-auto">
        <small>© {new Date().getFullYear()} SuperApp Admin • All rights reserved</small>
      </footer>
    </>
  );
}
